import { Injectable, inject } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import * as _ from "lodash-es";
import { environment } from "src/environments/environment";
import { AuthService } from "./auth.service";
import { MatDialog as MatDialog } from "@angular/material/dialog";
import { UploadingDialogComponent } from "../shared/components/dialog/uploading-dialog/uploading-dialog.component";
import { AlertService } from "./alert.service";
import { FilePathDescriptor } from "../models/easydiadem/request.model";
import { LivecoAPIService } from "./liveco-api.service";
import { saveBlob } from 'src/app/shared/utils/dom';
import { ViewRefresher } from "./view-refresher.service";


export interface IUploadingStatus {
    uploading: BehaviorSubject<boolean>,
    progression: {
        [key: string]: any;
    };
}

export interface IUploadOptions {
    progression?: boolean;
}

export interface IUploadedFile {
    destination: string,
    encoding: string,
    fieldname: string,
    filename: string,
    mimetype: string,
    originalname: string,
    path: string,
    cf_path: string,
    size: number;
    thumbnail?: {
        path: string,
        cf_path: string,
        mimetype: string,
        filename: string,
    };
}

interface IImgSRC {
    src: string;
}

@Injectable({ providedIn: 'root' })
export class FileService {
    protected auth = inject(AuthService);
    protected alert = inject(AlertService);
    // fetch() responses do not make Angular check the views without zone.js
    protected views = inject(ViewRefresher);
    protected dialog = inject(MatDialog);
    protected s_liveco = inject(LivecoAPIService);


    defaultOptions: IUploadOptions = {
        progression: true,
    };

    /** UPLOAD TO FILE REPOSITORY */
    protected async _uploadFile(file: File): Promise<IUploadedFile> {
        if (file) {
            // const formData = new FormData();
            // formData.append('file', file);

            const sanitizedFileName = _.kebabCase(file.name.replace(/\.[^/.]+$/, '')) + file.name.slice(file.name.lastIndexOf('.'));
            const newFile = new File([file], sanitizedFileName, { type: file.type });
    
            const formData = new FormData();            
            formData.append('file', newFile);

            try {
                const response = await fetch(
                    `${environment.api.url}/uploads`,
                    {
                        method: 'POST',
                        body: formData,
                        headers: {
                            "Authorization": `Bearer ${this.auth.accessToken}`
                        }
                    }
                );
                const result = await response.json();
                if (!_.isNil(result?.code) && result.code !== 200) {
                    throw result;
                }
                console.log('RESPONSE FILE', result);
                const data: IUploadedFile = result;
                return data;
            } catch (err: any) {
                console.error('[FILE UPLOAD]', err.message);
                throw err;
            } finally {
                this.views.refresh();
            }
        } else {
            throw new Error('[FILE UPLOAD] [no file]');
        }
    }

    /** DOWNLOAD FROM FILE REPOSITORY */
    protected async _downloadFile(fileDetails: IUploadedFile) {
        const url = `${environment.api.url}/download/${fileDetails.filename}`;
        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    "Authorization": `Bearer ${this.auth.accessToken}` // Assurez-vous d'inclure le jeton d'accès correct ici
                }
            });

            if (!response.ok) {
                throw new Error(`Server responded with ${response.status}: ${response.statusText}`);
            }

            // Créer un URL pour télécharger le fichier
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = fileDetails.originalname || 'downloadedFile';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(downloadUrl);
            a.remove();
        } catch (err) {
            console.error('[FILE DOWNLOAD]', err);
            throw err;
        } finally {
            this.views.refresh();
        }
    }

    /** CHECK IF FILE EXISTS */
    protected async _existFile(file_details: IUploadedFile) {
        const url = `${environment.api.url}/exists/${file_details.filename}`;
        try {

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    "Authorization": `Bearer ${this.auth.accessToken}` // Assurez-vous d'inclure le jeton d'accès correct ici
                }
            });

            if (!response.ok) {
                throw new Error(`Server responded with ${response.status}: ${response.statusText}`);
            }
            return _.assign(await response.json(), { file_details });
        } catch (err) {
            console.error('[FILE EXISTS]', err);
            throw err;
        } finally {
            this.views.refresh();
        }
    }

    public async existsFiles(file_details: IUploadedFile[]) {
        const promises = [];
        for (const file_detail of file_details) {
            promises.push(this._existFile(file_detail));
        }
        return await Promise.all(promises);
    }

    public async getFileThumbnailImg(thumbnail_filename: string) {
        return await this.s_liveco.get(thumbnail_filename, '/thumbnails');
    }

    public async downloadFile(fileDetails: IUploadedFile, options: IUploadOptions = this.defaultOptions) {
        let status;
        if (options.progression) {
            status = this.handleProgression('downloading');
        }
        try {
            const p = await this._downloadFile(fileDetails);
            return p;
        } finally {
            if (status)
                status.uploading.next(false);
        }
    }

    public downloadXlsx(buffer: string, filename: string) {
        const docname = filename + ".xlsx";

        console.log('buffer', buffer);

        const blob = this.base64ToBlob(buffer, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");

      
        
        // const blob = new Blob([buffer], {
        //     type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        // });

        saveBlob(blob, docname);
    }

    public base64ToBlob(base64: string, mimeType: string): Blob {
        // Décoder la chaîne Base64 en tableau binaire
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
    
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
    
        const byteArray = new Uint8Array(byteNumbers);
    
        // Créer un Blob à partir du tableau binaire
        return new Blob([byteArray], { type: mimeType });
    }

    public async downloadFiles(filesDetails: IUploadedFile[], options: IUploadOptions = this.defaultOptions) {
        let status;
        if (options.progression) {
            status = this.handleProgression('downloading');
        }
        const promises = [];
        for (const oneFileDetails of filesDetails) {
            promises.push(this.downloadFile(oneFileDetails));
        }
        try {
            const p = await Promise.all(promises);
            return p;
        } finally {
            if (status)
                status.uploading.next(false);
        }
    }

    public async uploadFile(file: File, options: IUploadOptions = this.defaultOptions): Promise<IUploadedFile> {
        let status;
        if (options.progression) {
            status = this.handleProgression('uploading');
        }
        try {
            const p = await this._uploadFile(file);
            return p;
        } finally {
            if (status)
                status.uploading.next(false);
        }
    }

    public async uploadFiles(files: File[], options: IUploadOptions = this.defaultOptions) {
        let status;
        if (options.progression) {
            status = this.handleProgression('uploading');
        }
        const promises = [];
        for (const oneFile of files) {
            promises.push(this.uploadFile(oneFile));
        }
        try {
            const p = await Promise.all(promises);
            return p;
        } finally {
            if (status)
                status.uploading.next(false);
        }

    }

    /**
     * Take a data (generally issued by a form), retrieve the file, upload them and return the given IUploaded file to the corresponding destination in object using a FilePathDescriptor 
     * @param data 
     * @returns 
     */
    public async manageFiles(data: any, file_path: FilePathDescriptor, options: IUploadOptions = this.defaultOptions) {
        let status;
        if (options?.progression) {
            status = this.handleProgression('uploading');
        }
        const files = [];
        try {
            for (let file_desc of file_path) {
                const filesList = _.get(data, file_desc.key);
                if (filesList instanceof File) {
                    // UPLOAD FILE PLAN
                    files.push({ ...file_desc, promise: this.uploadFile(filesList) });
                } if (filesList && _.isArray(filesList) && filesList.every(f => f instanceof File)) {
                    files.push({ ...file_desc, promise: this.uploadFiles(filesList) });
                }
            }
            const result = await Promise.all(files.map(async f => f.promise));
            for (let i = 0; i < files.length; i++) {
                if (result[i]) {
                    _.set(data, files[i].key, result[i]);
                }
            }
        } catch (err: any) {
            this.alert.error(err.message);
            throw err;
        } finally {
            if (status)
                status.uploading.next(false);
        }
        // this.alert.success('ED.FILES.UPLOAD_SUCCESS')
        return data;
    }


    public handleProgression(mode: 'uploading' | 'downloading' | 'archiving') {
        const status = {
            uploading: new BehaviorSubject<boolean>(true),
            progression: {},
        };
        this.openModal(status, mode);
        return status;
    }

    protected openModal(status: IUploadingStatus, mode: 'uploading' | 'downloading' | 'archiving') {
        this.dialog.open(UploadingDialogComponent, {
            data: {
                mode,
                status,
            }
        });
    }
}
